// Generates a square PNG blob (1080x1080) celebrating a milestone,
// drawn client-side on a <canvas>. 100% local, no server call.

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const candidate = line ? line + " " + w : w;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export async function generateMilestoneImage({ milestone, counterTitle }) {
  // Wait for custom fonts to be ready so Outfit / Manrope are rendered
  try {
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
  } catch {}

  const size = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  // Base background
  const bgGrad = ctx.createLinearGradient(0, 0, size, size);
  bgGrad.addColorStop(0, "#0F172A");
  bgGrad.addColorStop(0.5, "#1E293B");
  bgGrad.addColorStop(1, "#0B1120");
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, size, size);

  // Radial accents
  const r1 = ctx.createRadialGradient(size * 0.85, 0, 0, size * 0.85, 0, size * 0.9);
  r1.addColorStop(0, "rgba(249, 115, 22, 0.45)");
  r1.addColorStop(1, "rgba(249, 115, 22, 0)");
  ctx.fillStyle = r1;
  ctx.fillRect(0, 0, size, size);

  const r2 = ctx.createRadialGradient(size * 0.1, size, 0, size * 0.1, size, size * 0.9);
  r2.addColorStop(0, "rgba(16, 185, 129, 0.4)");
  r2.addColorStop(1, "rgba(16, 185, 129, 0)");
  ctx.fillStyle = r2;
  ctx.fillRect(0, 0, size, size);

  // Subtle noise dots
  ctx.fillStyle = "rgba(255,255,255,0.025)";
  for (let i = 0; i < 400; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    ctx.fillRect(x, y, 2, 2);
  }

  // Decorative blurred circles (simulated with semi-opaque fills)
  ctx.save();
  ctx.globalAlpha = 0.25;
  ctx.fillStyle = "#10B981";
  ctx.beginPath();
  ctx.arc(size * 0.85, size * 0.25, 110, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 0.18;
  ctx.fillStyle = "#F97316";
  ctx.beginPath();
  ctx.arc(size * 0.15, size * 0.75, 150, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Outer rounded border
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 2;
  roundRect(ctx, 20, 20, size - 40, size - 40, 48);
  ctx.stroke();

  // Brand block (logo + text)
  const logoSize = 80;
  const logoX = 80;
  const logoY = 80;
  const logoGrad = ctx.createLinearGradient(logoX, logoY, logoX + logoSize, logoY + logoSize);
  logoGrad.addColorStop(0, "#F97316");
  logoGrad.addColorStop(1, "#10B981");
  ctx.fillStyle = logoGrad;
  roundRect(ctx, logoX, logoY, logoSize, logoSize, 20);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.font = 'bold 42px "Outfit", system-ui, sans-serif';
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText("LÉ", logoX + logoSize / 2, logoY + logoSize / 2 + 2);

  ctx.textAlign = "left";
  ctx.fillStyle = "#F8FAFC";
  ctx.font = '600 36px "Outfit", system-ui, sans-serif';
  ctx.fillText("L'ÉQUIPE & MENTAL", logoX + logoSize + 24, logoY + 30);
  ctx.fillStyle = "#F97316";
  ctx.font = '500 22px "Manrope", system-ui, sans-serif';
  ctx.fillText("Écoute · Soutien · Bienveillance", logoX + logoSize + 24, logoY + 62);

  // Divider under header
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(80, 200);
  ctx.lineTo(size - 80, 200);
  ctx.stroke();

  // "PALIER FRANCHI" eyebrow with tier
  ctx.textAlign = "center";
  ctx.fillStyle = "#FDBA74";
  ctx.font = '700 30px "Outfit", system-ui, sans-serif';
  ctx.letterSpacing = "4px";
  const eyebrow = milestone.tier
    ? `· BADGE ${milestone.tier.toUpperCase()} ·`
    : "· PALIER FRANCHI ·";
  ctx.fillText(eyebrow, size / 2, 290);

  // Big emoji badge (if available)
  if (milestone.emoji) {
    ctx.fillStyle = "#F8FAFC";
    ctx.font = '400 120px "Outfit", "Apple Color Emoji", "Segoe UI Emoji", system-ui, sans-serif';
    ctx.fillText(milestone.emoji, size / 2, 420);
  }

  // Big number
  const parts = milestone.label.split(" ");
  const num = parts[0];
  const unit = parts.slice(1).join(" ") || "";
  ctx.fillStyle = "#F8FAFC";
  ctx.font = 'bold 240px "Outfit", system-ui, sans-serif';
  ctx.fillText(num, size / 2, 620);

  // Unit (JOURS / SEMAINES / MOIS / AN)
  ctx.fillStyle = milestone.color || "#10B981";
  ctx.font = '600 64px "Outfit", system-ui, sans-serif';
  ctx.fillText(unit.toUpperCase() || "JOUR", size / 2, 720);

  // Milestone title
  ctx.fillStyle = "#F8FAFC";
  ctx.font = 'bold 48px "Outfit", system-ui, sans-serif';
  const titleLines = wrapText(ctx, milestone.title, size - 200);
  titleLines.forEach((line, i) => {
    ctx.fillText(line, size / 2, 820 + i * 56);
  });

  // Counter title (quoted) in rounded tag
  if (counterTitle) {
    const quoted = `« ${counterTitle} »`;
    ctx.font = '500 30px "Manrope", system-ui, sans-serif';
    const tw = Math.min(ctx.measureText(quoted).width + 70, size - 160);
    const tagX = (size - tw) / 2;
    const tagY = 880 + Math.max(0, (titleLines.length - 1) * 56);
    ctx.fillStyle = "rgba(16, 185, 129, 0.12)";
    ctx.strokeStyle = "rgba(16, 185, 129, 0.4)";
    ctx.lineWidth = 2;
    roundRect(ctx, tagX, tagY, tw, 60, 30);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = "#A7F3D0";
    ctx.fillText(quoted, size / 2, tagY + 30);
  }

  // Footer tagline
  ctx.textAlign = "center";
  ctx.fillStyle = "#94A3B8";
  ctx.font = '500 26px "Manrope", system-ui, sans-serif';
  ctx.fillText("Un pas aujourd'hui, c'est 365 pas dans un an.", size / 2, size - 80);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
}
